import { z } from 'zod';

import { pageMetaSchema } from '@/lib/api/pagination';

/** Admin-side replacement statuses (CONTEXT.md, ADR-0019). `pending`/`approved`/`rejected`;
 *  approved/rejected are final. */
export const replacementStatusSchema = z.enum(['pending', 'approved', 'rejected']);

/** The six customer-facing replacement reasons (webstore US 112366, BA-confirmed). Wire
 *  tokens are snake_case; BE owns the final list. */
export const replacementReasonSchema = z.enum([
  'item_arrived_damaged',
  'missing_parts_or_accessories',
  'incorrect_item_delivered',
  'defective_or_malfunctioning',
  'poor_quality_not_as_pictured',
  'others',
]);

/** One row per requested replacement item (`ReplacementRequestGlobalListItem`). `phone`/
 *  `orderDate`/`replacementDate` match the real field names (renamed from `customerPhone`/
 *  `orderedAt`/`requestedAt`); no `customerAvatarUrl` exists on the wire. */
export const replacementRowSchema = z.object({
  id: z.string(),
  orderId: z.string(),
  orderNumber: z.string(),
  customerName: z.string(),
  phone: z.string(),
  orderDate: z.string(),
  city: z.string(),
  deviceCategory: z.string().nullable(),
  deviceName: z.string(),
  reason: replacementReasonSchema,
  replacementDate: z.string(),
  status: replacementStatusSchema,
  withInstallation: z.boolean().nullable(),
  hasApprovedInstallation: z.boolean(),
});

export const replacementListResponseSchema = z.object({
  data: z.array(replacementRowSchema),
  meta: pageMetaSchema,
});

/** Filter-drawer draft validation. All fields are multi-select arrays or nullable date
 *  bounds. Both date ranges must satisfy From <= To; the error attaches to the `*To` path
 *  with the AC's verbatim copy. (`enforceRangePairs` is number-only, so this is hand-written
 *  like `installationFiltersSchema`.) */
export const replacementFiltersSchema = z
  .object({
    customerIds: z.array(z.string()),
    deviceIds: z.array(z.string()),
    categoryIds: z.array(z.string()),
    cities: z.array(z.string()),
    statuses: z.array(replacementStatusSchema),
    reasons: z.array(replacementReasonSchema),
    orderDateFrom: z.string().nullable(),
    orderDateTo: z.string().nullable(),
    replacementDateFrom: z.string().nullable(),
    replacementDateTo: z.string().nullable(),
  })
  .superRefine((v, ctx) => {
    if (v.orderDateFrom && v.orderDateTo && v.orderDateFrom > v.orderDateTo) {
      ctx.addIssue({
        code: 'custom',
        path: ['orderDateTo'],
        message: 'End date must be after start date',
      });
    }
    if (
      v.replacementDateFrom &&
      v.replacementDateTo &&
      v.replacementDateFrom > v.replacementDateTo
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['replacementDateTo'],
        message: 'End date must be after start date',
      });
    }
  });

/** Filter-option shape for the customer/device meta selects (cursor typeaheads reusing the
 *  products-admin meta contract; `orderCustomerPageSchema`-identical shape). */
export const replacementOptionSchema = z.object({ id: z.string(), name: z.string() });

/** One page of a cursor-paginated typeahead (customers, devices). Mirrors `orderCustomerPageSchema`. */
export const replacementOptionPageSchema = z.object({
  data: z.array(replacementOptionSchema),
  meta: z.object({ nextCursor: z.string().optional(), hasNextPage: z.boolean() }),
});

/** Device Category filter options (`/products/admin/meta/categories`) — id-based, unlike the
 *  old name-only list; the filter field is `categoryIds`. */
export const replacementCategorySchema = z.object({ id: z.string(), name: z.string() });

export const replacementImageSchema = z.object({
  id: z.string(),
  url: z.string().nullable(),
  originalFileName: z.string().nullable(),
  sortOrder: z.number(),
});

const replacementAdminSummarySchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
});

/** Full replacement-request detail (`ReplacementRequestDetailResponse`) — item/claim-centric,
 *  not an extension of the row: no customer/order/address fields at all (the queue page carries
 *  those from the row it already fetched). Reason label comes pre-formatted from the backend. */
export const replacementDetailSchema = z.object({
  id: z.string(),
  itemName: z.string(),
  category: z.string().nullable(),
  imageUrl: z.string().nullable(),
  unitIndex: z.number(),
  quantity: z.number(),
  reason: replacementReasonSchema,
  reasonLabel: z.string(),
  description: z.string().nullable(),
  status: replacementStatusSchema,
  requestedAt: z.string(),
  requestedBy: replacementAdminSummarySchema.nullable(),
  isRequestedByCustomer: z.boolean(),
  resolvedAt: z.string().nullable(),
  resolvedBy: replacementAdminSummarySchema.nullable(),
  images: z.array(replacementImageSchema),
  rejectionReason: z.string().nullable(),
  replacementOrderId: z.string().nullable(),
  replacementOrderNumber: z.string().nullable(),
  orderId: z.string(),
  withInstallation: z.boolean().nullable(),
  hasApprovedInstallation: z.boolean(),
  installerId: z.string().nullable(),
  installerName: z.string().nullable(),
  externalInstallerName: z.string().nullable(),
  externalInstallerPhone: z.string().nullable(),
  installationOn: z.string().nullable(),
});

/** Reject-modal form validation (AC: required reason, hard cap 2000). The empty-reason message
 *  is AC-verbatim; the installation twin uses a >=3-char rule, this one only requires non-blank. */
export const rejectReplacementSchema = z.object({
  reason: z
    .string()
    .max(2000, 'Reason must be at most 2000 characters')
    .refine((s) => s.trim().length > 0, { message: 'Please enter a reason for rejection' }),
});
